import {
  useRef, useState, useEffect, useMemo, useCallback,
} from 'react';
import {
  Box,
  Card,
  CardMedia,
  IconButton,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PartnerProfileResourceType, PartnerResourceTranslation } from '../../../../common/models/PartnerProfile';
import { usePartnerProfileConfig } from '../Enrollment/usePartnerProfileConfig';
import { isResourcePreviewableInDialog } from '../../../../common/utils/partnerResourceUtils';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { useResourceDialog } from './useResourceDialog';
import { ResourcePreviewDialog } from './ResourcePreviewDialog';
import { getCanonicalPartnerName } from '../../../../common/constants';

type Props = {
  partnerName: string;
  partnerCode?: string;
};

export const PartnerResourcesSection = ({ partnerName, partnerCode }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { trackPartnerDetail } = useCustomEventTracking();
  const { openExternalLink } = useNavigationUtils();
  const { partnerMediaResources } = usePartnerProfileConfig();

  // Rename-stable name for deriving analytics event names (see getCanonicalPartnerName).
  const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);

  const resources = useMemo(
    () => [...(partnerMediaResources ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [partnerMediaResources],
  );

  const {
    videoDialog,
    pdfDialog,
    closeVideoDialog,
    closePdfDialog,
    openResourceByIndex,
    navigation,
    dialogResources,
  } = useResourceDialog(resources);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
    );
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
    }
    return () => {
      container?.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [resources, updateScrollButtons]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const firstCard = container.firstElementChild as HTMLElement | null;
    const cardWidth = firstCard?.offsetWidth ?? 0;
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const isVideo = (resource: PartnerResourceTranslation) => (
    resource.resourceType === PartnerProfileResourceType.VIDEO
  );

  const isDownloadable = (resource: PartnerResourceTranslation) => (
    resource.resourceType === PartnerProfileResourceType.PDF ||
    resource.resourceType === PartnerProfileResourceType.DOC
  );

  const getTrackingAction = (resource: PartnerResourceTranslation) => {
    switch (resource.resourceType) {
      case PartnerProfileResourceType.VIDEO:
        return `Watch${partnerKeyName}Video`;
      case PartnerProfileResourceType.PDF:
      case PartnerProfileResourceType.DOC:
      case PartnerProfileResourceType.CASE_STUDY:
        return `Preview${partnerKeyName}Resource`;
      default:
        return `View${partnerKeyName}Resource`;
    }
  };

  const handleResourceClick = (resource: PartnerResourceTranslation) => {
    trackPartnerDetail(
      'Click',
      getTrackingAction(resource),
      {
        resourceTitle: resource.title,
        resourceType: resource.resourceType,
        resourceLink: resource.resourceLink,
      },
    );

    const shouldOpenInDialog = isVideo(resource) ||
      isResourcePreviewableInDialog(resource.resourceType, resource.resourceLink);

    if (shouldOpenInDialog) {
      const dialogIndex = dialogResources.findIndex((r) => r === resource);
      openResourceByIndex(dialogIndex >= 0 ? dialogIndex : 0);
    } else {
      openExternalLink(resource.resourceLink);
    }
  };

  if (!resources || resources.length === 0) return null;

  const showScrollArrows = resources.length >= 3;

  return (
    <>
      <Box
        id="resources"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(2),
          alignSelf: 'stretch',
        }}
      >
        {/* Header with scroll arrows */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography variant="h4">{t('partnerDetails.resources')}</Typography>
          {showScrollArrows && (
            <Box sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
              <IconButton
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                size="small"
                sx={{
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: theme.spacing(1),
                  width: theme.spacing(4.5),
                  height: theme.spacing(4.5),
                  '&:disabled': {
                    opacity: 0.4,
                  },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                size="small"
                sx={{
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: theme.spacing(1),
                  width: theme.spacing(4.5),
                  height: theme.spacing(4.5),
                  '&:disabled': {
                    opacity: 0.4,
                  },
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Scrollable cards container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: theme.spacing(2),
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            width: '100%',
            pb: theme.spacing(1),
            // Hide scrollbar
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {resources.map((resource, index) => (
            <Card
              key={index}
              variant="outlined"
              data-testid={`partner-resource-card-${index}`}
              onClick={() => handleResourceClick(resource)}
              sx={{
                flex: '0 0 auto',
                width: {
                  xs: '84%',
                  sm: theme.spacing(37.5),
                },
                minWidth: {
                  xs: '84%',
                  sm: theme.spacing(37.5),
                },
                borderRadius: theme.spacing(1.5),
                cursor: 'pointer',
                scrollSnapAlign: 'start',
                overflow: 'hidden',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9 aspect ratio
                  backgroundColor: theme.palette.grey[100],
                  overflow: 'hidden',
                }}
              >
                {resource.thumbnailUrl && (
                  <CardMedia
                    component="img"
                    image={resource.thumbnailUrl}
                    alt={resource.title}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}

                {/* Video play icon overlay */}
                {isVideo(resource) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      width: theme.spacing(6),
                      height: theme.spacing(6),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    <PlayArrowIcon
                      sx={{
                        color: theme.palette.grey[900],
                        fontSize: 28,
                      }}
                    />
                  </Box>
                )}

                {/* Download icon for downloadable resources */}
                {isDownloadable(resource) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: theme.spacing(1),
                      right: theme.spacing(1),
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: theme.spacing(1),
                      width: theme.spacing(4),
                      height: theme.spacing(4),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <FileDownloadOutlinedIcon
                      sx={{
                        color: theme.palette.grey[800],
                        fontSize: 20,
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Card title */}
              <Box
                sx={{
                  p: theme.spacing(2),
                }}
              >
                <Typography
                  variant="h15"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {resource.title}
                </Typography>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
      <Divider />

      <ResourcePreviewDialog
        videoDialog={videoDialog}
        pdfDialog={pdfDialog}
        onCloseVideo={closeVideoDialog}
        onClosePdf={closePdfDialog}
        navigation={navigation}
      />
    </>
  );
};
